import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { notificationAPI } from '../services/api';
import { hasPermission, getRoleDisplay, isSystemRole } from '../utils/roleUtils';

function Navbar({ user, onLogout }) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Load unread notifications count for users with notification permissions
    const canViewNotifications = isSystemRole(user.role) ?
      (user.role === 'recruiter' || user.role === 'admin') :
      hasPermission(user.permissions, 'dashboard.view');

    if (canViewNotifications) {
      loadUnreadCount();

      // Set up interval to check for new notifications every 30 seconds
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user.permissions, user.role]);

  const loadUnreadCount = async () => {
    try {
      const isAdmin = isSystemRole(user.role) ?
        user.role === 'admin' :
        hasPermission(user.permissions, 'system.audit');
      const params = isAdmin ? { admin: true } : {};
      const response = await notificationAPI.getAll(params);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      // Silently fail for rate limit errors - don't show error for notification count
      if (!error.message?.includes('Too many requests')) {
        console.error('Failed to load notification count:', error);
      }
      // Set count to 0 on error to avoid UI issues
      setUnreadCount(0);
    }
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-brand">
          <img
            src="/assets/logo.png"
            alt="OUK Logo"
            className="navbar-logo"
          />
          <div className="navbar-brand-text">
            <span className="navbar-title">OUK</span>
            <span className="navbar-subtitle">Recruiter Management</span>
          </div>
        </div>

        <div className={`navbar-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* System roles use original hardcoded navigation */}
          {isSystemRole(user.role) ? (
            <>
              {user.role === 'admin' ? (
                <>
                  <Link
                    to="/admin"
                    className={`navbar-link ${isActive('/admin') && location.pathname === '/admin' ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/admin/recruiters"
                    className={`navbar-link ${isActive('/admin/recruiters') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Recruiters
                  </Link>
                  <Link
                    to="/admin/users"
                    className={`navbar-link ${isActive('/admin/users') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Users
                  </Link>
                  <Link
                    to="/admin/students"
                    className={`navbar-link ${isActive('/admin/students') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Students
                  </Link>
                  {/*
                  <Link
                    to="/admin/roles"
                    className={`navbar-link ${isActive('/admin/roles') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Roles & Permissions
                  </Link>
                  */}
                  <Link
                    to="/admin/payments"
                    className={`navbar-link ${isActive('/admin/payments') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Payments
                  </Link>
                  <Link
                    to="/admin/notifications"
                    className={`navbar-link ${isActive('/admin/notifications') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Notifications
                  </Link>
                  <Link
                    to="/admin/audit"
                    className={`navbar-link ${isActive('/admin/audit') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Audit Logs
                  </Link>
                </>
              ) : user.role === 'financial_officer' ? (
                <>
                  <Link
                    to="/financial"
                    className={`navbar-link ${isActive('/financial') && location.pathname === '/financial' ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/financial/uploads"
                    className={`navbar-link ${isActive('/financial/uploads') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Payment Uploads
                  </Link>
                  <Link
                    to="/financial/recruiters"
                    className={`navbar-link ${isActive('/financial/recruiters') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Recruiter Payments
                  </Link>
                  <Link
                    to="/financial/reports"
                    className={`navbar-link ${isActive('/financial/reports') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Reports
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/recruiter"
                    className={`navbar-link ${isActive('/recruiter') && location.pathname === '/recruiter' ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/recruiter/students"
                    className={`navbar-link ${isActive('/recruiter/students') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Students
                  </Link>
                  <Link
                    to="/recruiter/payments"
                    className={`navbar-link ${isActive('/recruiter/payments') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Payments
                  </Link>
                  <Link
                    to="/recruiter/notifications"
                    className={`navbar-link ${isActive('/recruiter/notifications') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Notifications
                  </Link>
                  <Link
                    to="/recruiter/leaderboard"
                    className={`navbar-link ${isActive('/recruiter/leaderboard') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    🏆 Leaderboard
                  </Link>
                  <Link
                    to="/recruiter/profile"
                    className={`navbar-link ${isActive('/recruiter/profile') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Profile
                  </Link>
                </>
              )}
            </>
          ) : (
            /* Custom roles use permission-based navigation */
            <>
              {(hasPermission(user.permissions, 'dashboard.view')) && (
                <Link
                  to="/admin"
                  className={`navbar-link ${isActive('/admin') && location.pathname === '/admin' ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              )}

              {(hasPermission(user.permissions, 'recruiters.view')) && (
                <Link
                  to="/admin/recruiters"
                  className={`navbar-link ${isActive('/admin/recruiters') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Recruiters
                </Link>
              )}

              {(hasPermission(user.permissions, 'users.view')) && (
                <Link
                  to="/admin/users"
                  className={`navbar-link ${isActive('/admin/users') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Users
                </Link>
              )}

              {(hasPermission(user.permissions, 'students.view')) && (
                <Link
                  to="/admin/students"
                  className={`navbar-link ${isActive('/admin/students') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Students
                </Link>
              )}

              {/* (hasPermission(user.permissions, 'system.roles')) && (
                <Link
                  to="/admin/roles"
                  className={`navbar-link ${isActive('/admin/roles') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Roles & Permissions
                </Link>
              ) */}

              {(hasPermission(user.permissions, 'payments.view')) && (
                <Link
                  to="/admin/payments"
                  className={`navbar-link ${isActive('/admin/payments') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Payments
                </Link>
              )}

              {(hasPermission(user.permissions, 'payments.upload')) && (
                <Link
                  to="/financial/uploads"
                  className={`navbar-link ${isActive('/financial/uploads') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Payment Uploads
                </Link>
              )}

              {(hasPermission(user.permissions, 'reports.view')) && (
                <Link
                  to="/financial/reports"
                  className={`navbar-link ${isActive('/financial/reports') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Reports
                </Link>
              )}

              {(hasPermission(user.permissions, 'students.view_own')) && (
                <Link
                  to="/recruiter/students"
                  className={`navbar-link ${isActive('/recruiter/students') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My Students
                </Link>
              )}

              {(hasPermission(user.permissions, 'students.view_own')) && (
                <Link
                  to="/recruiter/payments"
                  className={`navbar-link ${isActive('/recruiter/payments') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My Payments
                </Link>
              )}

              {(hasPermission(user.permissions, 'students.view_own')) && (
                <Link
                  to="/recruiter/leaderboard"
                  className={`navbar-link ${isActive('/recruiter/leaderboard') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  🏆 Leaderboard
                </Link>
              )}

              {(hasPermission(user.permissions, 'dashboard.view')) && (
                <Link
                  to="/admin/notifications"
                  className={`navbar-link ${isActive('/admin/notifications') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Notifications
                </Link>
              )}

              {(hasPermission(user.permissions, 'system.audit')) && (
                <Link
                  to="/admin/audit"
                  className={`navbar-link ${isActive('/admin/audit') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Audit Logs
                </Link>
              )}

              {(hasPermission(user.permissions, 'profile.view')) && (
                <Link
                  to="/recruiter/profile"
                  className={`navbar-link ${isActive('/recruiter/profile') ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile
                </Link>
              )}
            </>
          )}
        </div>

        <div className="navbar-actions">
          {(isSystemRole(user.role) ? (user.role === 'recruiter' || user.role === 'admin') : hasPermission(user.permissions, 'dashboard.view')) && (
            <Link
              to={user.role === 'admin' ? '/admin/notifications' : '/recruiter/notifications'}
              className="notification-icon-link"
              title={`${unreadCount} unread notifications`}
            >
              <div className="notification-icon-container">
                <span className="notification-icon">🔔</span>
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </div>
            </Link>
          )}
        </div>

        <div className="navbar-user">
          <div className="user-avatar">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.username)}&background=037b90&color=fff&size=32`}
              alt={user.username}
              className="user-avatar-img"
            />
          </div>
          <div className="user-info">
            <div className="user-name">{user.username}</div>
            <div className="user-role">{getRoleDisplay(user.role).displayName}</div>
          </div>
          <button onClick={onLogout} className="btn btn-outline btn-sm">
            Logout
          </button>
        </div>

        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </nav>
  );
}

export default Navbar;
